import { Spin, message } from 'antd';
import { cloneDeep } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';

import {
  useGetShareFunnel,
  useGetShareSankey,
  useShowFunnel,
  useShowSankey,
} from '@src/emp-3.0/api/queryHooks';
import { useFunnelStore } from '@src/emp-3.0/components/Funnel/store';
import { removeFunnelEmptyFilters } from '@src/emp-3.0/components/Funnel/utils';
import { useSankeyStore } from '@src/emp-3.0/components/Sankey/store';
import {
  addDefaultToEventBreakdown,
  removeSankeyEmptyFilters,
} from '@src/emp-3.0/components/Sankey/utils';
import { IFunnelRequestBody, ISankeyRequestBody } from '@src/emp-3.0/types';
import { useSearchParams } from 'react-router-dom';

interface IShareHoc {
  shareName: 'sankey' | 'funnel';
}

const isShareSankey = (shareName: string) => {
  return shareName === 'sankey';
};
const isShareFunnel = (shareName: string) => {
  return shareName === 'funnel';
};

const ShareHoc: React.FC<IShareHoc> = ({ shareName, children }) => {
  const [searchParams] = useSearchParams();
  const share_id = searchParams.get('share_id');
  const {
    setGenerateSankeyRequestBody,
    setSankeyRequestBody,
    setShowConfigModal,
    setSankeyData,
    setSelectedNodeIds,
    setCheckedOnly,
  } = useSankeyStore((state) => state);
  const { setFunnelRequestBody, setGenerateFunnelRequestBody, setFunnelData } = useFunnelStore(
    (state) => state,
  );

  const { data: sharedSankeyData, isFetching: isLoadingSharedSankeyData } = useGetShareSankey(
    share_id as string,
    {
      enabled: Boolean(share_id) && isShareSankey(shareName),
    },
  );
  const { data: sharedFunnelData, isFetching: isLoadingSharedFunnelData } = useGetShareFunnel(
    share_id as string,
    {
      enabled: Boolean(share_id) && isShareFunnel(shareName),
    },
  );
  const { mutate: getSankey, isLoading: isLoadingGetSankey } = useShowSankey();
  const { mutate: getFunnel, isLoading: isLoadingGetFunnel } = useShowFunnel();
  const loading =
    share_id &&
    (isLoadingSharedSankeyData ||
      isLoadingGetSankey ||
      isLoadingSharedFunnelData ||
      isLoadingGetFunnel);

  const fetchSankeyData = (payload: ISankeyRequestBody) => {
    const sankeyPayload = removeSankeyEmptyFilters(payload) as ISankeyRequestBody;
    getSankey(
      { payload: sankeyPayload },
      {
        onSuccess: (data) => {
          const { links = [], nodes = [] } = data?.data ?? {};
          if (links && nodes) {
            setSankeyData(data?.data);
          } else {
            setSankeyData({
              direction: 'forward',
              links: [],
              nodes: [],
            });
          }
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg).then();
        },
      },
    );
  };

  const fetchFunnelData = (payload: IFunnelRequestBody) => {
    const funnelPayload = removeFunnelEmptyFilters(payload);
    getFunnel(
      { payload: funnelPayload },
      {
        onSuccess: (data) => {
          if (data) {
            setFunnelData(data.data);
          } else {
            setFunnelData({
              funnel_data: [],
              event_names: [],
            });
          }
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg).then();
        },
      },
    );
  };

  useEffect(() => {
    if (isShareSankey(shareName) && sharedSankeyData) {
      const {
        selected_node_ids = [],
        checked_only = false,
        ...sankeyRequestBody
      } = sharedSankeyData;

      const eventBreakDown = addDefaultToEventBreakdown({
        event_breakdown: sankeyRequestBody?.event_breakdown,
        landing_event: sankeyRequestBody?.landing_events?.[0],
      });
      const updateSankeyRequestBody = {
        ...sankeyRequestBody,
        event_breakdown: eventBreakDown,
      };

      setShowConfigModal(false);
      setCheckedOnly(checked_only);
      setSelectedNodeIds(selected_node_ids);
      setGenerateSankeyRequestBody(cloneDeep(sharedSankeyData));
      setSankeyRequestBody(updateSankeyRequestBody);
      fetchSankeyData(sankeyRequestBody);
    }
  }, [shareName, sharedSankeyData]);

  useEffect(() => {
    if (isShareFunnel(shareName) && sharedFunnelData) {
      setFunnelRequestBody({
        ...sharedFunnelData,
        dimension_breakdowns: isEmpty(sharedFunnelData.dimension_breakdowns)
          ? [[{ field: '', op: 'eq', value: [] }]]
          : sharedFunnelData.dimension_breakdowns,
      });

      setGenerateFunnelRequestBody(cloneDeep(sharedFunnelData));
      fetchFunnelData(sharedFunnelData);
    }
  }, [shareName, sharedFunnelData]);

  return <Spin spinning={loading}>{children}</Spin>;
};

export default ShareHoc;
